import { isDefined } from "@clipboard-health/util-ts";
import {
  INTERVIEW_TYPE,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useMemo } from "react";

import { type PlacementDetail } from "../api/useGetPlacement";
import { getFormattedInterviewDate, isUnverifiedBookedInterview } from "../utils";

export interface InterviewMessage {
  title: string;
  subtitle?: string;
  hasBookedInterview: boolean;
}

interface UseInterviewMessageParams {
  interviews: PlacementDetail["interviews"];
  workerTimezone: string;
  hasActiveBan: boolean;
}

function getVerifiedAttendanceConfirmedMessage(params: {
  interview: PlacementDetail["interviews"][number];
  workerTimezone: string;
}): InterviewMessage {
  const { interview, workerTimezone } = params;
  const isPhoneScreen = interview.attributes.type === INTERVIEW_TYPE.PHONE_SCREEN;
  const interviewDate = getFormattedInterviewDate(interview, workerTimezone);
  return {
    hasBookedInterview: false,
    title: isPhoneScreen
      ? "You attended your phone call interview!"
      : "You attended your interview!",
    subtitle: `Attended on ${interviewDate}. If you still haven't heard back from the facility on next steps, click "Talk to a Recruiter" below and Clipboard will get in touch to help`,
  };
}

function getVerifiedNoShowMessage(params: {
  interview: PlacementDetail["interviews"][number];
  workerTimezone: string;
  hasActiveBan: boolean;
}): InterviewMessage {
  const { interview, workerTimezone, hasActiveBan } = params;
  const interviewDate = getFormattedInterviewDate(interview, workerTimezone);
  const baseSubtitle = `You no showed your interview on ${interviewDate}. You can book another interview at this facility`;
  return {
    hasBookedInterview: false,
    title: "You no showed your interview",
    subtitle: hasActiveBan ? `${baseSubtitle} once your restriction is complete` : baseSubtitle,
  };
}

function getCancelledMessage(isPhoneScreen: boolean): InterviewMessage {
  return {
    hasBookedInterview: false,
    title: isPhoneScreen
      ? "Your phone call interview was cancelled"
      : "Your interview was cancelled",
  };
}

function getUnverifiedMessage(params: {
  interview: PlacementDetail["interviews"][number];
  workerTimezone: string;
}): InterviewMessage {
  const { interview, workerTimezone } = params;
  const interviewDate = getFormattedInterviewDate(interview, workerTimezone);
  return {
    hasBookedInterview: false,
    title: "We're working to verify if you attended your interview",
    subtitle: `We weren't able to verify if you attended your interview on ${interviewDate} using location data. Click 'Verify Attendance' below to help us determine if you attended`,
  };
}

function getDefaultMessage(): InterviewMessage {
  return {
    hasBookedInterview: false,
    title: "Application submitted!",
    subtitle:
      "If the facility is interested in your qualifications, you'll receive an interview invite over email",
  };
}

function getBookedMessage(params: {
  interview: PlacementDetail["interviews"][number];
  workerTimezone: string;
}): InterviewMessage {
  const { interview, workerTimezone } = params;
  const isPhoneScreen = interview.attributes.type === INTERVIEW_TYPE.PHONE_SCREEN;
  const interviewDate = getFormattedInterviewDate(interview, workerTimezone);
  return {
    hasBookedInterview: true,
    title: isPhoneScreen ? "Your phone call interview is booked!" : "Your interview is booked!",
    subtitle: `Scheduled for ${interviewDate}`,
  };
}

export function useInterviewMessage(params: UseInterviewMessageParams): InterviewMessage {
  const { interviews, workerTimezone, hasActiveBan } = params;

  return useMemo(() => {
    const sortedInterviews = [...interviews].sort((a, b) => {
      return (
        new Date(b.attributes.createdAt).getTime() - new Date(a.attributes.createdAt).getTime()
      );
    });

    const lastInterview = sortedInterviews.at(0);
    if (!isDefined(lastInterview)) {
      return getDefaultMessage();
    }

    if (isUnverifiedBookedInterview(lastInterview)) {
      return getUnverifiedMessage({
        interview: lastInterview,
        workerTimezone,
      });
    }

    const isAttended = lastInterview.attributes.status === InterviewStatus.ATTENDANCE_CONFIRMED;
    if (isAttended) {
      return getVerifiedAttendanceConfirmedMessage({
        interview: lastInterview,
        workerTimezone,
      });
    }

    const isNoShow = lastInterview.attributes.status === InterviewStatus.NO_CALL_NO_SHOW;
    if (isNoShow) {
      return getVerifiedNoShowMessage({
        interview: lastInterview,
        workerTimezone,
        hasActiveBan,
      });
    }

    const isCancelled = lastInterview.attributes.status === InterviewStatus.CANCELLED;
    if (isCancelled) {
      return getCancelledMessage(lastInterview.attributes.type === INTERVIEW_TYPE.PHONE_SCREEN);
    }

    const isBooked = lastInterview.attributes.status === InterviewStatus.BOOKED;
    if (isBooked) {
      return getBookedMessage({
        interview: lastInterview,
        workerTimezone,
      });
    }

    return getDefaultMessage();
  }, [interviews, workerTimezone, hasActiveBan]);
}
